import { useEffect, useMemo, useState, SyntheticEvent, ChangeEvent } from "react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import axios from "axios";
import moment from "moment-timezone";
import { DateField, type DataTableColumn } from "@routes-plan/ui";
import { minYmd, nowOperationalDateString, operationalTimezone } from '../../utils/operationalDateTime';
import { isSuperAdminUser, parseUserFromApiResponse } from "../../utils/authScope";
import { Office } from "../../models/office";
import { Route } from "../../models/route";
import { useT } from "../../i18n";
import { setPageTitle } from "../../utils/pageTitle";
import { OfficeSelect, ReportEmpty, ReportFilters, ReportPage, ReportResults, ReportTable, RouteMultiSelect } from "./components/ReportLayout";

type SyncRow = Record<string, any> & {
  id: string;
  officeName: string;
  routeName: string;
  operatedDays: number;
  totalDays: number;
  transmissionPercentage: number;
};

interface TransmissionRowApi {
  officeId: number;
  officeName: string;
  routeId: number;
  routeName: string;
  operatedDays: number;
  totalDays: number;
  transmissionPercentage: number;
  dailyStatus: Record<string, string>;
}

interface TransmissionResponse {
  data: TransmissionRowApi[];
  dates: string[];
  meta?: {
    total?: number;
  };
}

interface ReporteTransmisionProps {
  showAlert: (
    message: string,
    severity?: "error" | "info" | "success" | "warning"
  ) => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const firstDayOfCurrentMonth = () =>
  moment.tz(nowOperationalDateString(), "YYYY-MM-DD", operationalTimezone()).startOf("month").format("YYYY-MM-DD");

const compareOfficeRoute = (a: { officeName?: unknown; routeName?: unknown }, b: { officeName?: unknown; routeName?: unknown }) => {
  const byOffice = String(a.officeName ?? "").localeCompare(String(b.officeName ?? ""), "es", {
    sensitivity: "base",
  });
  if (byOffice !== 0) return byOffice;
  return String(a.routeName ?? "").localeCompare(String(b.routeName ?? ""), "es", { sensitivity: "base" });
};

const AppSync: React.FC<ReporteTransmisionProps> = ({
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const t = useT();
  setPageTitle("menu.reports.app-sync");

  const [authUser, setAuthUser] = useState<{ isSystem?: unknown; username?: unknown } | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Route[]>([]);
  const [office_id, setOffice_id] = useState("");
  const [selectedCollects, setSelectedCollects] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [start_date, setStart_date] = useState(firstDayOfCurrentMonth());
  const [end_date, setEnd_date] = useState(nowOperationalDateString());
  const [dates, setDates] = useState<string[]>([]);
  const [rows, setRows] = useState<SyncRow[]>([]);
  const [queried, setQueried] = useState(false);
  const [loading, setLoading] = useState(false);
  const [todo, setTodo] = useState(false);

  const isSuperAdmin = isSuperAdminUser(authUser);
  const filtersDisabledForTodo = isSuperAdmin && todo;

  useEffect(() => {
    axios.get("offices").then(({ data }) => setOffices(data.data));
  }, []);

  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await axios.get("user");
        setAuthUser(parseUserFromApiResponse(data));
      } catch {
        setAuthUser(null);
      } finally {
        setAuthResolved(true);
      }
    };
    void run();
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) {
      setTodo(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (office_id) {
      axios.get(`offices/${office_id}/routes`).then(({ data }) => {
        setCollectsOffice(data);
        setSelectedCollects([]);
        setSelectAll(false);
      });
    }
  }, [office_id]);

  const handleTodoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setTodo(checked);
    if (checked) {
      setOffice_id("");
      setSelectedCollects([]);
      setSelectAll(false);
      setCollectsOffice([]);
    }
  };

  const validateForm = () => {
    if (!authResolved) {
      showAlert(t("auditReports.appSync.sessionLoading"), "warning");
      return false;
    }

    if (!start_date || !end_date) {
      showAlert(t("auditReports.appSync.dateRangeRequired"), "warning");
      return false;
    }

    if (start_date > end_date) {
      showAlert(t("auditReports.appSync.startAfterEnd"), "warning");
      return false;
    }

    if (isSuperAdmin && todo) {
      return true;
    }

    if (!office_id) {
      showAlert(t("auditReports.appSync.officeRequired"), "warning");
      return false;
    }

    if (!selectedCollects || selectedCollects.length === 0) {
      showAlert(t("auditReports.appSync.routeRequired"), "warning");
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    showLoading();
    setLoading(true);
    setQueried(true);
    try {
      const payload: Record<string, unknown> = {
        startDate: start_date,
        endDate: end_date,
      };

      const scopeAllSystem = isSuperAdmin && todo;
      if (!scopeAllSystem) {
        payload.officeId = Number(office_id);
        payload.routeIds = selectedCollects.map((id) => Number(id));
      }

      const response = await axios.post<TransmissionResponse>("/reports/app-sync", payload);
      const apiRows = Array.isArray(response.data?.data) ? response.data.data : [];
      const apiDates = Array.isArray(response.data?.dates) ? response.data.dates : [];

      const mappedRows: SyncRow[] = apiRows
        .map((row) => {
          const dynamicStatus = apiDates.reduce<Record<string, string>>((acc, date) => {
            acc[`day_${date}`] = row.dailyStatus?.[date] || "";
            return acc;
          }, {});

          return {
            id: `${row.officeId}-${row.routeId}`,
            officeName: row.officeName,
            routeName: row.routeName,
            operatedDays: row.operatedDays,
            totalDays: row.totalDays,
            transmissionPercentage: row.transmissionPercentage,
            ...dynamicStatus,
          };
        })
        .sort(compareOfficeRoute);

      setDates(apiDates);
      setRows(mappedRows);

      if (mappedRows.length === 0) {
        showAlert(t("auditReports.appSync.noData"), "info");
      } else {
        showAlert(t("auditReports.appSync.found", { count: mappedRows.length }), "success");
      }
    } catch (error) {
      console.error("Error obteniendo informe de transmisión:", error);
      showAlert(t("auditReports.appSync.loadError"), "error");
      setDates([]);
      setRows([]);
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  const columns = useMemo<DataTableColumn<SyncRow>[]>(() => {
    const baseColumns: DataTableColumn<SyncRow>[] = [
      { id: "officeName", header: t("auditReports.appSync.colOffice"), value: (r) => r.officeName, sortable: true },
      { id: "routeName", header: t("summaryReports.shared.route"), value: (r) => r.routeName, sortable: true },
      { id: "operatedDays", header: t("auditReports.appSync.colOperatedDays"), value: (r) => Number(r.operatedDays ?? 0), align: "right", sortable: true },
      { id: "totalDays", header: t("auditReports.appSync.colTotalDays"), value: (r) => Number(r.totalDays ?? 0), align: "right", sortable: true, hideBelow: "sm" },
      {
        id: "transmissionPercentage",
        header: t("auditReports.appSync.colPercentage"),
        value: (r) => Number(r.transmissionPercentage ?? 0),
        render: (r) => `${Number(r.transmissionPercentage ?? 0).toFixed(2)}%`,
        csv: (r) => `${Number(r.transmissionPercentage ?? 0).toFixed(2)}%`,
        align: "right",
        sortable: true,
      },
    ];
    const dateColumns: DataTableColumn<SyncRow>[] = dates.map((date) => {
      const label = moment.tz(date, "YYYY-MM-DD", operationalTimezone()).format("DD/MM");
      return {
        id: `day_${date}`,
        header: label,
        csvHeader: `${t("auditReports.appSync.dailyGroup")} ${label}`,
        value: (r) => r[`day_${date}`] || "",
        align: "center" as const,
      };
    });
    return [...baseColumns, ...dateColumns];
  }, [dates, t]);

  return (
    <ReportPage title={t("menu.reports.app-sync")} description={t("auditReports.appSync.description")}>
      <ReportFilters onSubmit={() => handleSubmit({ preventDefault: () => undefined } as SyntheticEvent)} submitDisabled={!authResolved || loading}>
        <DateField
          label={t("auditReports.startDate")}
          value={start_date}
          onChange={(v) => setStart_date(v ?? "")}
          max={minYmd(end_date, nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t("auditReports.endDate")}
          value={end_date}
          onChange={(v) => setEnd_date(v ?? "")}
          min={start_date}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={office_id} onChange={setOffice_id} disabled={filtersDisabledForTodo} />
        <RouteMultiSelect
          routes={collectsOffice}
          value={selectedCollects}
          onChange={(ids) => {
            setSelectedCollects(ids);
            setSelectAll(ids.length > 0 && ids.length === collectsOffice.length);
          }}
          disabled={filtersDisabledForTodo}
        />
        {authResolved && isSuperAdmin && (
          <FormControlLabel
            control={<Checkbox checked={todo} onChange={handleTodoChange} />}
            label={t("auditReports.appSync.allOfficesAndRoutes")}
            sx={{ m: 0, minHeight: 40 }}
          />
        )}
      </ReportFilters>

      <ReportResults>
        <ReportTable<SyncRow>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          exportFileName={t("auditReports.appSync.exportFileName")}
          ariaLabel={t("menu.reports.app-sync")}
          emptyState={<ReportEmpty queried={queried} />}
        />
      </ReportResults>
    </ReportPage>
  );
};

export default AppSync;
